import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { 
  EnvelopeIcon, 
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  BuildingLibraryIcon,
  DocumentIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import FileDropzone from '../components/common/FileDropzone';

export default function AdhesionDetail() {
  const [adherent, setAdherent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidForm, setMarkPaidForm] = useState({
    typePaiement: 'cheque',
    datePaiement: new Date().toISOString().slice(0, 10),
    note: '',
    documentPaiement: null,
  });
  const [uploadingDocument] = useState(false);
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);
  const [downloadingAttestation, setDownloadingAttestation] = useState(false);
  const [downloadingServiceAttestation, setDownloadingServiceAttestation] = useState(false);
  const [miellerieService, setMiellerieService] = useState(null);
  const [unafService, setUnafService] = useState(null);
  const [showCautionModal, setShowCautionModal] = useState(false);
  const [showServiceMarkPaidModal, setShowServiceMarkPaidModal] = useState(false);
  const [currentServiceForPayment, setCurrentServiceForPayment] = useState(null); // 'miellerie' ou 'unaf'
  const [currentUnafTransactionIndex, setCurrentUnafTransactionIndex] = useState(null); // null = paiement initial, number = index de modification
  const [serviceMarkPaidForm, setServiceMarkPaidForm] = useState({
    typePaiement: 'cheque',
    datePaiement: new Date().toISOString().slice(0, 10),
    note: '',
    documentPaiement: null,
  });
  const [serviceMarkPaidSubmitting, setServiceMarkPaidSubmitting] = useState(false);
  const [cautionForm, setCautionForm] = useState({
    status: 'recu',
    dateReception: new Date().toISOString().slice(0, 10),
    note: '',
    documentCaution: null,
  });
  const [cautionSubmitting, setCautionSubmitting] = useState(false);
  const [uploadingCautionDocument] = useState(false);
  const [requestingPayment, setRequestingPayment] = useState(false);
  const [showDocumentTypeSelectionModal, setShowDocumentTypeSelectionModal] = useState(false);
  const [showReuploadDocumentModal, setShowReuploadDocumentModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [reuploadingDocument, setReuploadingDocument] = useState(false);
  const [newDocumentFile, setNewDocumentFile] = useState(null);
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [abandonSubmitting, setAbandonSubmitting] = useState(false);
  const [downloadingAllDocuments, setDownloadingAllDocuments] = useState(false);
  const [regeneratingBulletin, setRegeneratingBulletin] = useState(false);
  const [downloadingBulletin, setDownloadingBulletin] = useState(false);
  const [nextUnafExportDate, setNextUnafExportDate] = useState(null);
  const { id } = useParams();
  const { user } = useAuth();
  const { canChangeAdherentStatus } = usePermissions();
  const [userRoles, setUserRoles] = useState([]);
  const isAdmin = user && ['admin', 'super_admin'].includes(user.role);
  const isSuperAdmin = user && user.role === 'super_admin';
  const navigate = useNavigate();


  const handleDownloadBulletin = async () => {
    setDownloadingBulletin(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/bulletin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur téléchargement bulletin:', error);
      alert(error.response?.data?.message || 'Erreur lors du téléchargement du bulletin');
    } finally {
      setDownloadingBulletin(false);
    }
  };

  const handleRegenerateBulletin = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir régénérer le bulletin d\'adhésion ? L\'ancien bulletin sera supprimé et remplacé par un nouveau.')) {
      return;
    }

    setRegeneratingBulletin(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/regenerate-bulletin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Mettre à jour les données de l'adhérent avec le nouveau bulletin
      setAdherent(prev => ({
        ...prev,
        bulletinKey: response.data.bulletinKey,
        bulletinUrl: response.data.bulletinUrl
      }));
      
      alert('Bulletin d\'adhésion régénéré avec succès !');
    } catch (error) {
      console.error('Erreur lors de la régénération du bulletin:', error);
      alert(error.response?.data?.message || 'Erreur lors de la régénération du bulletin');
    } finally {
      setRegeneratingBulletin(false);
    }
  };

  const handleDownloadAllDocuments = async () => {
    setDownloadingAllDocuments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/download-all-documents`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis les headers ou utiliser un nom par défaut
      const contentDisposition = response.headers['content-disposition'];
      let filename = `documents-adhesion-${adherent.organisme}-${adherent.annee}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement des documents:', error);
      alert('Erreur lors du téléchargement des documents');
    } finally {
      setDownloadingAllDocuments(false);
    }
  };

  const handleReuploadDocument = async () => {
    if (!newDocumentFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setReuploadingDocument(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', newDocumentFile);
      formData.append('documentType', selectedDocumentType);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/add-document`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const docTypeLabel = selectedDocumentType === 'declaration_ruches' ? 'Déclaration de ruches' : 'Attestation CGSS';
      alert(`${docTypeLabel} ajoutée avec succès`);
      setShowReuploadDocumentModal(false);
      setNewDocumentFile(null);
      setSelectedDocumentType(null);
      
      // Recharger les données de l'adhésion sans recharger la page
      const responseAdherent = await axios.get(`${process.env.REACT_APP_API_URL}/adhesions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdherent(responseAdherent.data);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du document:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'ajout du document');
    } finally {
      setReuploadingDocument(false);
    }
  };

  const handleDownloadAttestation = async () => {
    setDownloadingAttestation(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/attestation`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Ouvrir l'URL signée dans un nouvel onglet
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur téléchargement attestation:', error);
      alert(error.response?.data?.message || 'Erreur lors du téléchargement de l\'attestation');
    } finally {
      setDownloadingAttestation(false);
    }
  };

  const handleDownloadServiceAttestation = async () => {
    if (!miellerieService?.attestationKey) return;
    
    setDownloadingServiceAttestation(true);
    try {
      const token = localStorage.getItem('token');
      // Récupérer l'URL signée pour visualisation
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/files/signed-url/${encodeURIComponent(miellerieService.attestationKey)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Ouvrir l'URL signée dans un nouvel onglet
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur ouverture attestation service:', error);
      alert('Erreur lors de l\'ouverture de l\'attestation de service');
    } finally {
      setDownloadingServiceAttestation(false);
    }
  };

  const openMarkPaidModal = () => {
    setMarkPaidForm({
      typePaiement: 'cheque',
      datePaiement: new Date().toISOString().slice(0, 10),
      note: '',
      documentPaiement: null,
    });
    setShowMarkPaidModal(true);
  };

  const openServiceMarkPaidModal = (serviceType, transactionIndex = null) => {
    setCurrentServiceForPayment(serviceType);
    setCurrentUnafTransactionIndex(transactionIndex);
    setServiceMarkPaidForm({
      typePaiement: 'cheque',
      datePaiement: new Date().toISOString().slice(0, 10),
      note: '',
      documentPaiement: null,
    });
    setShowServiceMarkPaidModal(true);
  };

  const handleServiceMarkPaid = async (e) => {
    e.preventDefault();
    
    const service = currentServiceForPayment === 'miellerie' ? miellerieService : unafService;
    if (!service) return;
    
    try {
      setServiceMarkPaidSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Utiliser FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('typePaiement', serviceMarkPaidForm.typePaiement);
      formData.append('datePaiement', serviceMarkPaidForm.datePaiement);
      formData.append('note', serviceMarkPaidForm.note);
      
      if (serviceMarkPaidForm.documentPaiement) {
        formData.append('documentPaiement', serviceMarkPaidForm.documentPaiement);
      }
      
      // Pour les services UNAF avec modifications, ajouter l'index de la transaction
      if (currentServiceForPayment === 'unaf' && currentUnafTransactionIndex !== null) {
        formData.append('modificationIndex', currentUnafTransactionIndex);
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/service/mark-paid/${service._id}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      if (currentServiceForPayment === 'miellerie') {
        setMiellerieService(response.data.service);
      } else {
        setUnafService(response.data.service);
      }
      setShowServiceMarkPaidModal(false);
      setCurrentUnafTransactionIndex(null);
    } catch (error) {
      console.error('Erreur lors du marquage du paiement service:', error);
      alert(error.response?.data?.message || 'Erreur lors du marquage du paiement');
    } finally {
      setServiceMarkPaidSubmitting(false);
    }
  };

  const handleMarkPaid = async (e) => {
    e.preventDefault();
    try {
      setMarkPaidSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Utiliser FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('typePaiement', markPaidForm.typePaiement);
      formData.append('datePaiement', markPaidForm.datePaiement);
      formData.append('note', markPaidForm.note);
      
      if (markPaidForm.documentPaiement) {
        formData.append('documentPaiement', markPaidForm.documentPaiement);
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/mark-paid/${id}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      setAdherent(response.data.adhesion);
      setShowMarkPaidModal(false);
    } catch (error) {
      console.error('Erreur lors du marquage du paiement:', error);
      alert(error.response?.data?.message || 'Erreur lors du marquage du paiement');
    } finally {
      setMarkPaidSubmitting(false);
    }
  };

  const openAbandonModal = () => {
    setShowAbandonModal(true);
  };

  const handleAbandonAdhesion = async () => {
    try {
      setAbandonSubmitting(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/adhesions/${id}/abandon`,
        {},
        { 
          headers: { 
            Authorization: `Bearer ${token}`
          } 
        }
      );

      setAdherent(response.data);
      setShowAbandonModal(false);
      alert('L\'adhésion a été marquée comme abandonnée');
    } catch (error) {
      console.error('Erreur lors de l\'abandon de l\'adhésion:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'abandon de l\'adhésion');
    } finally {
      setAbandonSubmitting(false);
    }
  };

  useEffect(() => {
    fetchAdherent();
    fetchNextUnafExportDate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAdherent = async () => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/adhesions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Adhésion récupérée:', response.data);
      console.log('Documents:', response.data.documents);
      setAdherent(response.data);
      setError(null);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'adhésion:', error);
      setError('Erreur lors du chargement de l\'adhésion');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextUnafExportDate = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/public/next-unaf-export?annee=${currentYear}`);
      setNextUnafExportDate(response.data.nextExportDateFormatted);
    } catch (err) {
      console.error('Erreur lors du chargement de la prochaine date d\'export UNAF:', err);
    }
  };

  // Récupérer le service miellerie lié à cette adhésion (si AMAIR)
  useEffect(() => {
    const fetchMiellerieService = async () => {
      if (!adherent || adherent.organisme !== 'AMAIR') return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/services/adhesion/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Trouver le service miellerie
        const miellerie = response.data.find(s => s.typeService === 'miellerie');
        setMiellerieService(miellerie || null);
      } catch (error) {
        console.error('Erreur lors de la récupération du service miellerie:', error);
      }
    };

    fetchMiellerieService();
  }, [adherent, id]);

  // Récupérer le service UNAF lié à cette adhésion (si SAR)
  useEffect(() => {
    const fetchUnafService = async () => {
      if (!adherent || adherent.organisme !== 'SAR') return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/services/adhesion/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Trouver le service UNAF
        const unaf = response.data.find(s => s.typeService === 'assurance_unaf');
        setUnafService(unaf || null);
      } catch (error) {
        console.error('Erreur lors de la récupération du service UNAF:', error);
      }
    };

    fetchUnafService();
  }, [adherent, id]);

  // Récupérer les rôles statutaires de l'utilisateur
  useEffect(() => {
    const fetchUserRoles = async () => {
      if (!adherent?.user?._id) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/composition/user/${adherent.user._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUserRoles(response.data.rolesStatutaires || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des rôles:', error);
      }
    };

    fetchUserRoles();
  }, [adherent]);

  const formatTitre = (titre) => titre.charAt(0).toUpperCase() + titre.slice(1);
  const formatFonction = (fonction) => fonction ? fonction.charAt(0).toUpperCase() + fonction.slice(1) : '';

  const openCautionModal = (currentStatus) => {
    setCautionForm({
      status: currentStatus === 'en_attente' ? 'recu' : 'rendu',
      dateReception: new Date().toISOString().slice(0, 10),
      note: miellerieService?.caution?.note || '',
      documentCaution: null,
    });
    setShowCautionModal(true);
  };

  const handleUpdateCaution = async (e) => {
    e.preventDefault();
    try {
      setCautionSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Utiliser FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('status', cautionForm.status);
      if (cautionForm.status === 'recu' && cautionForm.dateReception) {
        formData.append('dateReception', cautionForm.dateReception);
      }
      formData.append('note', cautionForm.note);
      
      if (cautionForm.documentCaution) {
        formData.append('documentCaution', cautionForm.documentCaution);
      }
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/services/${miellerieService._id}/caution`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          } 
        }
      );

      setMiellerieService(response.data.service);
      setShowCautionModal(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la caution:', error);
      alert(error.response?.data?.message || 'Erreur lors de la mise à jour de la caution');
    } finally {
      setCautionSubmitting(false);
    }
  };

  const getCautionStatusLabel = (status) => {
    const labels = {
      'en_attente': 'En attente',
      'recu': 'Reçu',
      'rendu': 'Rendu',
    };
    return labels[status] || status;
  };

  const getCautionStatusColor = (status) => {
    const colors = {
      'en_attente': 'bg-honey-100 text-honey-800',
      'recu': 'bg-green-100 text-green-800',
      'rendu': 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleRequestPayment = async () => {
    setRequestingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/request-payment`,
        { adhesionId: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAdherent({
        ...adherent,
        status: response.data.adhesion.status,
        paiement: response.data.adhesion.paiement
      });
      
      alert('Demande de paiement envoyée avec succès !');
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande de paiement:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'envoi de la demande de paiement');
    } finally {
      setRequestingPayment(false);
    }
  };

  // Ouvrir un document dans un nouvel onglet (via URL signée)
  const handleOpenDocument = async (doc) => {
    if (!doc.s3Key) {
      // Fallback: ouvrir l'URL directement
      window.open(doc.url, '_blank');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      // Récupérer l'URL signée pour visualisation
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/files/signed-url/${encodeURIComponent(doc.s3Key)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Ouvrir l'URL signée dans un nouvel onglet
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur lors de l\'ouverture du document:', error);
      // Fallback: ouvrir l'URL directement si disponible
      if (doc.url) {
        window.open(doc.url, '_blank');
      } else {
        alert('Erreur lors de l\'ouverture du document');
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Non renseignée';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'actif':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'en_attente':
        return <ClockIcon className="h-5 w-5 text-honey-500" />;
      case 'refuse':
      case 'abandonnee':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'en_cours': 'En cours',
      'en_attente': 'En attente',
      'actif': 'Actif',
      'refuse': 'Refusé',
      'abandonnee': 'Abandonnée'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'en_cours': 'bg-yellow-100 text-yellow-800',
      'en_attente': 'bg-honey-100 text-honey-800',
      'actif': 'bg-green-100 text-green-800',
      'refuse': 'bg-red-100 text-red-800',
      'abandonnee': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!adherent) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Adhérent non trouvé</h3>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <button
            onClick={() => navigate('/adhesions')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <span className="font-medium text-gray-900">Abeille Réunion</span>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <button onClick={() => navigate('/adhesions')} className="hover:text-gray-700">
            Adhésions
          </button>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>{adherent.user?.prenom} {adherent.user?.nom}</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {adherent.user?.prenom} {adherent.user?.nom}
              </h1>
              <p className="text-sm text-gray-500 mt-1">{adherent.user?.email}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {getStatusIcon(adherent.status)}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(adherent.status)}`}>
              {getStatusLabel(adherent.status)}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">

            {/* Informations statutaires */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <BuildingLibraryIcon className="h-5 w-5 text-indigo-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Informations statutaires</h2>
                </div>
              </div>
              <div className="px-6 py-4">
                {(() => {
                  const rolesOrganisme = userRoles.filter(r => r.organisme === adherent?.organisme);
                  
                  if (rolesOrganisme.length === 0) {
                    return (
                      <div className="flex items-center text-gray-600">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                          Adhérent
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-2">
                      {rolesOrganisme.map((role, index) => (
                        <div key={index} className="flex items-center py-2 px-3 bg-indigo-50 rounded-lg">
                          <span className="font-medium text-indigo-900">
                            {formatTitre(role.titre)}
                          </span>
                          {role.fonction && (
                            <span className="ml-2 text-indigo-600">
                              — {formatFonction(role.fonction)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Afficher aussi les rôles dans d'autres organismes */}
                {userRoles.filter(r => r.organisme !== adherent?.organisme).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">Rôles dans d'autres organismes :</p>
                    {userRoles.filter(r => r.organisme !== adherent?.organisme).map((role, index) => (
                      <div key={index} className="text-sm text-gray-600">
                        <span className="font-medium">{role.organisme}</span> : {formatTitre(role.titre)}
                        {role.fonction && ` — ${formatFonction(role.fonction)}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Informations personnelles */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Informations personnelles</h2>
                <p className="text-xs text-gray-500 mt-1">Informations au moment de l'adhésion</p>
              </div>
              <div className="px-6 py-4">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Type</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {(() => {
                        const type = adherent.informationsPersonnelles?.typePersonne || adherent.user?.typePersonne || 'personne_physique';
                        const labels = { personne_physique: 'Personne physique', association: 'Association', scea: 'SCEA', earl: 'EARL', etablissement_public: 'Établissement public' };
                        return labels[type] || type;
                      })()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Civilité</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {adherent.informationsPersonnelles?.designation || adherent.user?.designation || 'Non renseigné'}
                    </dd>
                  </div>
                  {['association', 'scea', 'earl', 'etablissement_public'].includes(adherent.informationsPersonnelles?.typePersonne || adherent.user?.typePersonne) && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Raison sociale</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {adherent.informationsPersonnelles?.raisonSociale || adherent.user?.raisonSociale || 'Non renseigné'}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      {['association', 'scea', 'earl', 'etablissement_public'].includes(adherent.informationsPersonnelles?.typePersonne || adherent.user?.typePersonne) ? 'Prénom du représentant légal' : 'Prénom'}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {adherent.informationsPersonnelles?.prenom || adherent.user?.prenom || 'Non renseigné'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      {['association', 'scea', 'earl', 'etablissement_public'].includes(adherent.informationsPersonnelles?.typePersonne || adherent.user?.typePersonne) ? 'Nom du représentant légal' : 'Nom'}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {adherent.informationsPersonnelles?.nom || adherent.user?.nom || 'Non renseigné'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {adherent.informationsPersonnelles?.email || adherent.user?.email || 'Non renseigné'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Date de naissance</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(adherent.informationsPersonnelles?.dateNaissance || adherent.user?.dateNaissance)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Téléphone mobile</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {adherent.informationsPersonnelles?.telephoneMobile || adherent.user?.telephoneMobile || 'Non renseigné'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Téléphone fixe</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {adherent.informationsPersonnelles?.telephone || adherent.user?.telephone || 'Non renseigné'}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Adresse</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {(adherent.informationsPersonnelles?.adresse?.rue || adherent.user?.adresse?.rue) || 'Non renseigné'}<br />
                      {(adherent.informationsPersonnelles?.adresse?.codePostal || adherent.user?.adresse?.codePostal)} {(adherent.informationsPersonnelles?.adresse?.ville || adherent.user?.adresse?.ville)}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Informations apicoles */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Informations apicoles</h2>
              </div>
              <div className="px-6 py-4">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Nombre de ruches</dt>
                    <dd className="mt-1 text-sm text-gray-900">{adherent.nombreRuches || 'Non renseigné'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Nombre d'emplacements</dt>
                    <dd className="mt-1 text-sm text-gray-900">{adherent.nombreRuchers || 'Non renseigné'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Département</dt>
                    <dd className="mt-1 text-sm text-gray-900">{adherent.localisation?.departement ? adherent.localisation.departement : "–"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Commune</dt>
                    <dd className="mt-1 text-sm text-gray-900">{adherent.localisation?.commune ? adherent.localisation.commune : "–"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Numéro NAPI</dt>
                    <dd className="mt-1 text-sm text-gray-900">{adherent.napi ? adherent.napi : "–"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Numéro AMEXA</dt>
                    <dd className="mt-1 text-sm text-gray-900">{adherent.numeroAmexa ? adherent.numeroAmexa : "–"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">SIRET</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {adherent.siret ? adherent.siret : "–"}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Adhésion AMAIR gratuite pour adhérents SAR */}
            {adherent.organisme === 'SAR' && adherent.adhesionAMAIRGratuite && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Adhésion à l'AMAIR</h2>
                </div>
                <div className="px-6 py-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-yellow-900">
                          Adhésion gratuite à l'AMAIR incluse
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Documents */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <button
                      onClick={handleDownloadAllDocuments}
                      disabled={downloadingAllDocuments}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      title="Télécharger tous les documents en ZIP"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {downloadingAllDocuments ? 'Téléchargement...' : 'Tout télécharger'}
                    </button>
                  )}
                  {isAdmin && adherent.documents?.some(doc => doc.type === 'declaration_ruches') && (
                    <button
                      onClick={() => setShowDocumentTypeSelectionModal(true)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-honey-600 hover:bg-honey-700 rounded-md transition-colors"
                      title="Ajouter un nouveau document"
                    >
                      Ajouter un nouveau document
                    </button>
                  )}
                </div>
              </div>
              <div className="px-6 py-4">
                <ul className="divide-y divide-gray-200">
                  {/* Bulletin d'adhésion */}
                  {adherent.bulletinKey && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Bulletin d'adhésion</p>
                        <p className="text-sm text-gray-500">Bulletin {adherent.organisme} - Année {adherent.annee}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isSuperAdmin && (
                          <button
                            onClick={handleRegenerateBulletin}
                            disabled={regeneratingBulletin}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-honey-600 hover:bg-honey-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Régénérer le bulletin d'adhésion"
                          >
                            {regeneratingBulletin ? 'Régénération...' : 'Regénérer'}
                          </button>
                        )}
                        <button
                          onClick={handleDownloadBulletin}
                          disabled={downloadingBulletin}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Télécharger le bulletin d'adhésion"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </li>
                  )}

                  {/* Attestation d'adhésion */}
                  {adherent.status === 'actif' && adherent.attestationKey && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Attestation d'adhésion</p>
                        <p className="text-sm text-gray-500">Attestation officielle {adherent.organisme} - Année {adherent.annee}</p>
                      </div>
                      <button
                        onClick={handleDownloadAttestation}
                        disabled={downloadingAttestation}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Télécharger l'attestation d'adhésion"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    </li>
                  )}

                  {/* Extrait du règlement intérieur miellerie (signé) */}
                  {miellerieService?.reglementMiellerieKey && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Extrait du règlement intérieur de la miellerie</p>
                        <p className="text-sm text-gray-500">Annexe au règlement intérieur de l&apos;AMAIR - Année {miellerieService.annee}</p>
                      </div>
                      <button
                        onClick={() => handleOpenDocument({ s3Key: miellerieService.reglementMiellerieKey, nomOriginal: `extrait-reglement-miellerie-${miellerieService.annee}.pdf` })}
                        className="p-2 text-honey-600 hover:bg-honey-50 rounded-lg transition-colors"
                        title="Télécharger l'extrait du règlement intérieur"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    </li>
                  )}

                  {/* Attestation de souscription au service miellerie */}
                  {miellerieService?.status === 'actif' && miellerieService?.attestationKey && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Attestation service miellerie</p>
                        <p className="text-sm text-gray-500">Attestation de souscription - Année {miellerieService.annee}</p>
                      </div>
                      <button
                        onClick={handleDownloadServiceAttestation}
                        disabled={downloadingServiceAttestation}
                        className="p-2 text-honey-600 hover:bg-honey-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Télécharger l'attestation de service"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    </li>
                  )}

                  {/* Attestation de souscription au service UNAF */}
                  {unafService?.status === 'actif' && unafService?.attestationKey && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Attestation services UNAF</p>
                        <p className="text-sm text-gray-500">Attestation d'assurance - Année {unafService.annee}</p>
                      </div>
                      <button
                        onClick={() => handleOpenDocument({ s3Key: unafService.attestationKey, nomOriginal: `attestation-assurance-unaf-${unafService.annee}.pdf` })}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Voir l'attestation UNAF"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    </li>
                  )}

                  {/* Attestation d'écocontribution UNAF */}
                  {unafService?.status === 'actif' && unafService?.ecocontributionAttestationKey && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Attestation écocontribution UNAF</p>
                        <p className="text-sm text-gray-500">Attestation d'écocontribution - Année {unafService.annee}</p>
                      </div>
                      <button
                        onClick={() => handleOpenDocument({ s3Key: unafService.ecocontributionAttestationKey, nomOriginal: `attestation-ecocontribution-unaf-${unafService.annee}.pdf` })}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Voir l'attestation écocontribution"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    </li>
                  )}
                  
                  {/* Documents uploadés par l'adhérent */}
                  { (adherent.documents && adherent.documents.length > 0) ? (() => {
                    // Calculer la déclaration de ruches la plus récente
                    const declarationsRuches = adherent.documents.filter(d => d.type === 'declaration_ruches');
                    const plusRecenteDeclaration = declarationsRuches.length > 0 
                      ? declarationsRuches.reduce((latest, current) => {
                          return new Date(current.uploadDate) > new Date(latest.uploadDate) ? current : latest;
                        }, declarationsRuches[0])
                      : null;
                    
                    // Calculer l'attestation CGSS la plus récente
                    const attestationsCGSS = adherent.documents.filter(d => d.type === 'releve_exploitation_cgss');
                    const plusRecenteAttestationCGSS = attestationsCGSS.length > 0 
                      ? attestationsCGSS.reduce((latest, current) => {
                          return new Date(current.uploadDate) > new Date(latest.uploadDate) ? current : latest;
                        }, attestationsCGSS[0])
                      : null;
                    
                    return adherent.documents.map((doc, index) => (
                      <li key={doc.s3Key || index} className="py-3 flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-gray-900 capitalize">{doc.type.replace('_', ' ')}</p>
                          <p className="text-sm text-gray-500">{doc.nomOriginal || doc.nom}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.type === 'declaration_ruches' && plusRecenteDeclaration && doc.s3Key === plusRecenteDeclaration.s3Key && (
                            <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-md">
                              Plus récente
                            </span>
                          )}
                          {doc.type === 'releve_exploitation_cgss' && plusRecenteAttestationCGSS && doc.s3Key === plusRecenteAttestationCGSS.s3Key && (
                            <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-md">
                              Plus récente
                            </span>
                          )}
                        <button
                          onClick={() => handleOpenDocument(doc)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Télécharger le document"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </li>
                  ));
                  })() : !adherent.attestationKey && !adherent.bulletinKey && <span className="text-slate-600 text-sm italic">Pas de documents disponibles.</span> }
                </ul>
              </div>
            </div>

            {/* Reçus et preuves de paiement (admin only) */}
            {isAdmin && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Reçus et preuves de paiement</h2>
                  <p className="text-sm text-gray-500 mt-1">Visible uniquement par les administrateurs</p>
                </div>
                <div className="px-6 py-4">
                  <ul className="divide-y divide-gray-200">
                    {/* Document de preuve de paiement adhésion */}
                    {adherent.documentPaiement?.key && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Preuve de paiement - Adhésion</p>
                        <p>
                          <span className="text-sm text-gray-500">{adherent.documentPaiement.nom}</span>
                          <span className="px-2 text-gray-200">|</span>
                          <span className="text-xs text-gray-400">Uploadé le {new Date(adherent.documentPaiement.dateUpload).toLocaleDateString('fr-FR')}</span>
                        </p>
                      </div>
                      <a
                        href={adherent.documentPaiement.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                        title="Télécharger la preuve de paiement"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </a>
                    </li>
                  )}

                  {/* Reçu Stripe - Adhésion */}
                  {adherent.paiement?.receiptUrl && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Reçu de paiement Stripe - Adhésion</p>
                        <p className="text-sm text-gray-500">Reçu officiel du paiement en ligne</p>
                      </div>
                      <a
                        href={adherent.paiement.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Voir le reçu Stripe"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      </a>
                    </li>
                  )}

                  {/* Document de preuve de paiement service miellerie */}
                  {miellerieService?.documentPaiement?.key && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Preuve de paiement - Service Miellerie</p>
                        <p>
                          <span className="text-sm text-gray-500">{miellerieService.documentPaiement.nom}</span>
                          <span className="px-2 text-gray-200">|</span>
                          <span className="text-xs text-gray-400">Uploadé le {new Date(miellerieService.documentPaiement.dateUpload).toLocaleDateString('fr-FR')}</span>
                        </p>
                      </div>
                      <a
                        href={miellerieService.documentPaiement.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-honey-600 hover:bg-honey-100 rounded-lg transition-colors"
                        title="Télécharger la preuve de paiement"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </a>
                    </li>
                  )}

                  {/* Document de preuve de caution service miellerie */}
                  {miellerieService?.caution?.documentPreuve?.key && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Preuve de dépôt de caution - Service Miellerie</p>
                        <p>
                          <span className="text-sm text-gray-500">{miellerieService.caution.documentPreuve.nom}</span>
                          <span className="px-2 text-gray-200">|</span>
                          <span className="text-xs text-gray-400">Uploadé le {new Date(miellerieService.caution.documentPreuve.dateUpload).toLocaleDateString('fr-FR')}</span>
                        </p>
                      </div>
                      <button
                        onClick={() => handleOpenDocument({ s3Key: miellerieService.caution.documentPreuve.key, nomOriginal: miellerieService.caution.documentPreuve.nom })}
                        className="p-2 text-honey-600 hover:bg-honey-100 rounded-lg transition-colors"
                        title="Télécharger la preuve de caution"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                    </li>
                  )}

                  {/* Document de preuve de paiement service UNAF */}
                  {unafService?.documentPaiement?.key && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Preuve de paiement - Services de l'UNAF</p>
                        <p>
                          <span className="text-sm text-gray-500">{unafService.documentPaiement.nom}</span>
                          <span className="px-2 text-gray-200">|</span>
                          <span className="text-xs text-gray-400">Uploadé le {new Date(unafService.documentPaiement.dateUpload).toLocaleDateString('fr-FR')}</span>
                        </p>
                      </div>
                      <a
                        href={unafService.documentPaiement.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-honey-600 hover:bg-honey-100 rounded-lg transition-colors"
                        title="Télécharger la preuve de paiement"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </a>
                    </li>
                  )}

                  {/* Reçu Stripe - Service UNAF */}
                  {unafService?.receiptUrl && (
                    <li className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Reçu de paiement Stripe - Services de l'UNAF</p>
                        <p className="text-sm text-gray-500">Reçu officiel du paiement initial</p>
                      </div>
                      <a
                        href={unafService.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Voir le reçu Stripe"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      </a>
                    </li>
                  )}

                  {/* Reçus Stripe - Modifications UNAF */}
                  {unafService?.historiqueModifications?.filter(m => m.paiement?.receiptUrl).map((modif, index) => (
                    <li key={`receipt-modif-${index}`} className="py-3 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Reçu Stripe - Modification UNAF</p>
                        <p className="text-sm text-gray-500">
                          Modification du {new Date(modif.date).toLocaleDateString('fr-FR')} - {modif.montantSupplementaire?.toFixed(2)} €
                        </p>
                      </div>
                      <a
                        href={modif.paiement.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Voir le reçu Stripe"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      </a>
                    </li>
                  ))}

                  {!adherent.documentPaiement?.key && !adherent.paiement?.receiptUrl && !miellerieService?.documentPaiement?.key && !unafService?.documentPaiement?.key && !unafService?.receiptUrl && (
                    <span className="text-slate-600 text-sm italic">Aucun reçu de paiement disponible.</span>
                  )}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Organisme et Année */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Adhésion</h2>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center justify-center gap-3 p-4 bg-indigo-50 rounded-lg">
                  <span className="text-2xl font-bold text-indigo-600">{adherent.organisme}</span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-lg font-bold bg-honey-500 text-white shadow-sm">
                    {adherent.annee}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-600 text-center">
                  {adherent.organisme === 'SAR' 
                    ? 'Syndicat Apicole de la Réunion' 
                    : adherent.organisme === 'ADAR'
                    ? 'Association pour le Développement de l\'Apiculture à L\'île de la Réunion'
                    : adherent.organisme === 'AMAIR'
                    ? 'Association de la Maison de l\'Apiculture de l\'Île de la Réunion'
                    : ''}
                </p>
              </div>
            </div>

            {/* Paiement */}
            {adherent.paiement && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Paiement</h2>
                </div>
                <div className="px-6 py-4 space-y-4">
                  {adherent.paiement.montant !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Montant</span>
                      <span className="text-lg font-bold text-indigo-600">{adherent.paiement.montant.toFixed(2)} €</span>
                    </div>
                  )}
                  
                  {adherent.paiement.typePaiement && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Mode</span>
                      <span className="text-sm text-gray-900">
                        {adherent.paiement.typePaiement === 'cheque' ? 'Chèque' : adherent.paiement.typePaiement === 'virement' ? 'Virement' : adherent.paiement.typePaiement === 'espece' ? 'Espèce' : adherent.paiement.typePaiement === 'gratuit' ? 'Gratuit' : 'En ligne'}
                      </span>
                    </div>
                  )}

                  {adherent.paiement.datePaiement && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Date</span>
                      <span className="text-sm text-gray-900">{formatDate(adherent.paiement.datePaiement)}</span>
                    </div>
                  )}

                  {adherent.paiement.note && (
                    <div>
                      <div className="text-sm text-gray-500">Note</div>
                      <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{adherent.paiement.note}</div>
                    </div>
                  )}

                  {/* Bouton de demande de paiement */}
                  {adherent.paiement.typePaiement === 'en_ligne' && (
                    <div className="pt-4 border-t border-gray-200">
                      {adherent.status === 'en_attente' && adherent.paiement.status === "non_demande" ? (
                        <button
                          type="button"
                          onClick={handleRequestPayment}
                          disabled={requestingPayment}
                          className={`w-full inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white transition-colors ${requestingPayment ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                        >
                          {requestingPayment ? (
                            <>
                              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <EnvelopeIcon className="h-5 w-5 mr-2" />
                              Demander le paiement
                            </>
                          )}
                        </button>
                      ) : adherent.status === 'paiement_demande' && adherent.paiement.status === 'demande' ? (
                        <div className="text-center">
                          <div className="inline-flex items-center px-3 py-2 bg-honey-50 text-honey-700 rounded-md text-sm">
                            <ClockIcon className="h-5 w-5 mr-2" />
                            En attente de paiement
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Demande envoyée le {formatDate(adherent.paiement.dateEnvoiLien)}
                          </p>
                        </div>
                      ) : adherent.status === 'actif' && adherent.paiement.status === "paye" ? (
                        <div className="text-center">
                          <div className="inline-flex flex-col items-center px-3 py-2 bg-green-50 text-green-700 rounded-md text-sm">
                            <div className="flex items-center">
                              <CheckCircleIcon className="h-5 w-5 mr-2" />
                              Paiement effectué
                            </div>
                            {adherent.paiement?.datePaiement && (
                              <div className="text-xs text-green-600 mt-1">
                                le {new Date(adherent.paiement.datePaiement).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null }
                    </div>
                  )}
                  
                  {canChangeAdherentStatus() && adherent.status !== 'abandonnee' && adherent.paiement.status !== 'paye' && (
                    <div>
                      <button
                        type="button"
                        onClick={openMarkPaidModal}
                        className="w-full inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
                      >
                        Marquer le paiement effectué
                      </button>
                    </div>
                  )}
                  
                  {canChangeAdherentStatus() && (
                    <div>
                      {adherent.status === 'abandonnee' ? (
                        <div className="w-full inline-flex justify-center items-center py-3 px-4 bg-red-50 border border-red-200 rounded-md">
                          <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                          <span className="text-sm font-medium text-red-800">
                            Adhésion abandonnée
                          </span>
                        </div>
                      ) : adherent.status === 'en_attente' || adherent.status === 'paiement_demande' ? (
                        <button
                          type="button"
                          onClick={openAbandonModal}
                          className="w-full inline-flex justify-center items-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                          Abandon de l'adhésion
                        </button>
                      ) : null }
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service Miellerie - Seulement pour AMAIR */}
            {adherent.organisme === 'AMAIR' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center">
                    <WrenchScrewdriverIcon className="h-5 w-5 text-honey-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Service Miellerie</h2>
                  </div>
                </div>
                <div className="px-6 py-4 space-y-4">
                  {miellerieService ? (
                    <>
                      {/* Statut du service */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Statut</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          miellerieService.status === 'actif' ? 'bg-green-100 text-green-800' :
                          miellerieService.status === 'en_attente_caution' ? 'bg-honey-100 text-honey-800' :
                          'bg-honey-100 text-honey-800'
                        }`}>
                          {miellerieService.status === 'actif' ? 'Actif' :
                           miellerieService.status === 'en_attente_caution' ? 'En attente caution' :
                           'En attente paiement'}
                        </span>
                      </div>

                      {/* Paiement du service */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Paiement (25 €)</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          miellerieService.paiement?.status === 'paye' ? 'bg-green-100 text-green-800' : 'bg-honey-100 text-honey-800'
                        }`}>
                          {miellerieService.paiement?.status === 'paye' ? 'Payé' : 'En attente'}
                        </span>
                      </div>

                      {miellerieService.paiement?.datePaiement && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">Date paiement</span>
                          <span className="text-sm text-gray-900">{formatDate(miellerieService.paiement.datePaiement)}</span>
                        </div>
                      )}

                      {/* Bouton marquer comme payé pour le service */}
                      {canChangeAdherentStatus() && miellerieService.paiement?.status !== 'paye' && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => openServiceMarkPaidModal('miellerie')}
                            className="w-full inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
                          >
                            Marquer le paiement effectué
                          </button>
                        </div>
                      )}

                      {/* Caution */}
                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Chèque de caution (300 €)</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCautionStatusColor(miellerieService.caution?.status)}`}>
                            {getCautionStatusLabel(miellerieService.caution?.status)}
                          </span>
                        </div>

                        {miellerieService.caution?.dateReception && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500">Date réception</span>
                            <span className="text-gray-900">{formatDate(miellerieService.caution.dateReception)}</span>
                          </div>
                        )}

                        {miellerieService.caution?.dateRendu && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500">Date rendu</span>
                            <span className="text-gray-900">{formatDate(miellerieService.caution.dateRendu)}</span>
                          </div>
                        )}

                        {miellerieService.caution?.note && (
                          <div className="mt-2">
                            <span className="text-sm text-gray-500">Note :</span>
                            <p className="text-sm text-gray-900 mt-1">{miellerieService.caution.note}</p>
                          </div>
                        )}

                        {/* Boutons de gestion de la caution */}
                        {canChangeAdherentStatus() && miellerieService.caution?.status !== 'rendu' && (
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => openCautionModal(miellerieService.caution?.status)}
                              className={`w-full inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white transition-colors ${
                                miellerieService.caution?.status === 'en_attente' 
                                  ? 'bg-green-600 hover:bg-green-700' 
                                  : 'bg-blue-600 hover:bg-blue-700'
                              }`}
                            >
                              {miellerieService.caution?.status === 'en_attente' 
                                ? 'Marquer caution reçue' 
                                : 'Marquer caution rendue'}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <ExclamationTriangleIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Non inscrit aux services de la miellerie</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Service UNAF - Seulement pour SAR */}
            {adherent.organisme === 'SAR' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center">
                    <BuildingLibraryIcon className="h-5 w-5 text-green-600 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Services de l'UNAF</h2>
                  </div>
                </div>
                <div className="px-6 py-4 space-y-4">
                  {unafService ? (
                    <>
                      {/* Statut du service */}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Statut</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          unafService.status === 'actif' ? 'bg-green-100 text-green-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {unafService.status === 'actif' ? 'Actif' : unafService.status === 'en_attente_validation' ? 'En attente de validation' : 'En attente paiement'}
                        </span>
                      </div>

                      {/* Prochaine date d'export si en attente de validation */}
                      {unafService.status === 'en_attente_validation' && nextUnafExportDate && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <CalendarIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              <p className="text-blue-900">
                                <span className="font-medium">📅 La prochaine transmission des souscriptions à l'UNAF est prévue en date du</span> <strong>{nextUnafExportDate}</strong>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Détails UNAF */}
                      {unafService.unafData && (
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Nombre de ruches</span>
                            <span className="text-gray-900">{unafService.unafData.nombreRuches}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Nombre d'emplacements</span>
                            <span className="text-gray-900">{unafService.unafData.nombreEmplacements}</span>
                          </div>
                          {unafService.unafData.siret && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">SIRET</span>
                              <span className="text-gray-900">{unafService.unafData.siret}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-500">Formule actuelle</span>
                            <span className="text-gray-900 font-medium">{unafService.unafData.options?.assurance?.formule?.replace('formule', 'Formule ') || '-'}</span>
                          </div>
                          {unafService.unafData.options?.revue?.choix && unafService.unafData.options.revue.choix !== 'aucun' && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Revue</span>
                              <span className="text-gray-900">
                                {unafService.unafData.options.revue.choix === 'papier_numerique' ? 'Papier + Numérique' : 
                                 unafService.unafData.options.revue.choix.charAt(0).toUpperCase() + unafService.unafData.options.revue.choix.slice(1)}
                              </span>
                            </div>
                          )}
                          {unafService.unafData.options?.affairesJuridiques?.souscrit && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Affaires juridiques</span>
                              <span className="text-green-600">Oui</span>
                            </div>
                          )}
                          {unafService.unafData.options?.ecocontribution?.souscrit && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Écocontribution</span>
                              <span className="text-green-600">Oui</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Section Paiements */}
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Paiements</p>
                        <div className="space-y-2">
                          {/* Paiement initial */}
                          <div className={`p-2 rounded text-xs ${unafService.paiement?.status === 'paye' ? 'bg-green-50 border border-green-200' : 'bg-honey-50 border border-honey-200'}`}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-gray-700 font-medium">Souscription initiale</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${unafService.paiement?.status === 'paye' ? 'bg-green-100 text-green-700' : 'bg-honey-100 text-honey-700'}`}>
                                {unafService.paiement?.status === 'paye' ? 'Payé' : 'En attente'}
                              </span>
                            </div>
                            {unafService.unafData?.optionsInitiales && (
                              <p className="text-gray-600 mb-1">
                                {unafService.unafData.optionsInitiales.assurance?.formule?.replace('formule', 'Formule ')}
                                {unafService.unafData.optionsInitiales.revue?.choix && unafService.unafData.optionsInitiales.revue.choix !== 'aucun' && ' • Revue'}
                                {unafService.unafData.optionsInitiales.affairesJuridiques?.souscrit && ' • AJ'}
                                {unafService.unafData.optionsInitiales.ecocontribution?.souscrit && ' • Éco'}
                              </p>
                            )}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">{unafService.paiement?.datePaiement ? formatDate(unafService.paiement.datePaiement) : ''}</span>
                              <span className="font-medium text-honey-600">{unafService.paiement?.montant?.toFixed(2)} €</span>
                            </div>
                            {/* Bouton marquer comme payé pour paiement initial */}
                            {canChangeAdherentStatus() && unafService.paiement?.status !== 'paye' && (
                              <button
                                type="button"
                                onClick={() => openServiceMarkPaidModal('unaf', null)}
                                className="mt-2 w-full inline-flex justify-center items-center py-1 px-2 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 transition-colors"
                              >
                                Marquer comme payé
                              </button>
                            )}
                          </div>

                          {/* Modifications */}
                          {unafService.historiqueModifications && unafService.historiqueModifications.map((modif, index) => (
                            <div key={index} className={`p-2 rounded text-xs ${modif.paiement?.status === 'paye' ? 'bg-green-50 border border-green-200' : 'bg-honey-50 border border-honey-200'}`}>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-gray-700 font-medium">Modification #{index + 1}</span>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${modif.paiement?.status === 'paye' ? 'bg-green-100 text-green-700' : 'bg-honey-100 text-honey-700'}`}>
                                  {modif.paiement?.status === 'paye' ? 'Payé' : 'En attente'}
                                </span>
                              </div>
                              <div className="text-gray-600 mb-1">
                                {modif.nouvellesOptions?.assurance?.formule && modif.nouvellesOptions.assurance.formule !== modif.anciennesOptions?.assurance?.formule && (
                                  <span>{modif.anciennesOptions?.assurance?.formule?.replace('formule', 'F')} → {modif.nouvellesOptions.assurance.formule.replace('formule', 'F')}</span>
                                )}
                                {modif.nouvellesOptions?.revue?.choix && modif.nouvellesOptions.revue.choix !== 'aucun' && (!modif.anciennesOptions?.revue?.choix || modif.anciennesOptions.revue.choix === 'aucun') && (
                                  <span> • +Revue</span>
                                )}
                                {modif.nouvellesOptions?.affairesJuridiques?.souscrit && !modif.anciennesOptions?.affairesJuridiques?.souscrit && (
                                  <span> • +AJ</span>
                                )}
                                {modif.nouvellesOptions?.ecocontribution?.souscrit && !modif.anciennesOptions?.ecocontribution?.souscrit && (
                                  <span> • +Éco</span>
                                )}
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-500">{modif.paiement?.datePaiement ? formatDate(modif.paiement.datePaiement) : formatDate(modif.date)}</span>
                                <span className="font-medium text-indigo-600">+{modif.montantSupplementaire?.toFixed(2)} €</span>
                              </div>
                              {/* Bouton marquer comme payé pour chaque modification */}
                              {canChangeAdherentStatus() && modif.paiement?.status !== 'paye' && (
                                <button
                                  type="button"
                                  onClick={() => openServiceMarkPaidModal('unaf', index)}
                                  className="mt-2 w-full inline-flex justify-center items-center py-1 px-2 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 transition-colors"
                                >
                                  Marquer comme payé
                                </button>
                              )}
                            </div>
                          ))}

                          {/* Total si modifications */}
                          {unafService.historiqueModifications && unafService.historiqueModifications.length > 0 && (
                            <div className="pt-2 border-t border-gray-300 flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Total</span>
                              <span className="text-sm font-bold text-honey-600">
                                {((unafService.paiement?.montant || 0) + (unafService.historiqueModifications?.filter(m => m.paiement?.status === 'paye').reduce((sum, m) => sum + (m.montantSupplementaire || 0), 0) || 0)).toFixed(2)} €
                              </span>
                            </div>
                          )}
                        </div>
                      </div>


                      {/* Attestation */}
                      {unafService.status === 'actif' && unafService.attestationKey && (
                        <div className="pt-3 border-t border-gray-200">
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await axios.get(
                                  `${process.env.REACT_APP_API_URL}/files/download/${encodeURIComponent(unafService.attestationKey)}`,
                                  { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                                );
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `attestation-assurance-unaf-${unafService.annee}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (error) {
                                console.error('Erreur téléchargement attestation UNAF:', error);
                              }
                            }}
                            className="w-full inline-flex justify-center items-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                            Télécharger l'attestation
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <ExclamationTriangleIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Non inscrit aux services de l'UNAF</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showMarkPaidModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Marquer le paiement effectué
              </h3>
              <button
                type="button"
                onClick={() => setShowMarkPaidModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleMarkPaid} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mode de paiement
                </label>
                <select
                  value={markPaidForm.typePaiement}
                  onChange={(e) => setMarkPaidForm({ ...markPaidForm, typePaiement: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="cheque">Chèque</option>
                  <option value="virement">Virement</option>
                  <option value="espece">Espèce</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  required
                  value={markPaidForm.datePaiement}
                  onChange={(e) => setMarkPaidForm({ ...markPaidForm, datePaiement: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optionnelle)
                </label>
                <textarea
                  value={markPaidForm.note}
                  onChange={(e) => setMarkPaidForm({ ...markPaidForm, note: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document justificatif (optionnel)
                </label>
                <FileDropzone
                  onFileSelect={(file) => setMarkPaidForm({ ...markPaidForm, documentPaiement: file })}
                  uploading={uploadingDocument}
                  uploadedFile={markPaidForm.documentPaiement ? { name: markPaidForm.documentPaiement.name } : null}
                  label="Ajouter un justificatif de paiement"
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ex: copie du chèque, reçu, etc. (PDF, JPG, PNG - max 10MB)
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowMarkPaidModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={markPaidSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {markPaidSubmitting ? 'Enregistrement...' : 'Valider'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal marquer paiement service effectué */}
      {showServiceMarkPaidModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Marquer le paiement du service effectué
              </h3>
              <button
                type="button"
                onClick={() => setShowServiceMarkPaidModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleServiceMarkPaid} className="space-y-4">
              {(() => {
                const service = currentServiceForPayment === 'miellerie' ? miellerieService : unafService;
                const serviceName = currentServiceForPayment === 'miellerie' 
                  ? 'Services de la miellerie' 
                  : 'Services de l\'UNAF';
                const bgColor = currentServiceForPayment === 'miellerie' ? 'bg-honey-50' : 'bg-indigo-50';
                const textColor = currentServiceForPayment === 'miellerie' ? 'text-honey-800' : 'text-indigo-800';
                
                // Pour UNAF, déterminer le montant selon la transaction
                let montant = service?.paiement?.montant || 0;
                let transactionLabel = 'Souscription initiale';
                
                if (currentServiceForPayment === 'unaf' && currentUnafTransactionIndex !== null) {
                  const modif = service?.historiqueModifications?.[currentUnafTransactionIndex];
                  montant = modif?.montantSupplementaire || 0;
                  transactionLabel = `Modification #${currentUnafTransactionIndex + 1}`;
                }
                
                return (
                  <div className={`${bgColor} p-3 rounded-lg mb-4`}>
                    <p className={`text-sm ${textColor}`}>
                      <strong>Service :</strong> {service?.nom || serviceName}
                    </p>
                    {currentServiceForPayment === 'unaf' && (
                      <p className={`text-sm ${textColor}`}>
                        <strong>Transaction :</strong> {transactionLabel}
                      </p>
                    )}
                    <p className={`text-sm ${textColor}`}>
                      <strong>Montant :</strong> {montant.toFixed(2)} €
                    </p>
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mode de paiement
                </label>
                <select
                  value={serviceMarkPaidForm.typePaiement}
                  onChange={(e) => setServiceMarkPaidForm({ ...serviceMarkPaidForm, typePaiement: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-honey-500"
                >
                  <option value="cheque">Chèque</option>
                  <option value="virement">Virement</option>
                  <option value="en_ligne">En ligne</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de paiement
                </label>
                <input
                  type="date"
                  required
                  value={serviceMarkPaidForm.datePaiement}
                  onChange={(e) => setServiceMarkPaidForm({ ...serviceMarkPaidForm, datePaiement: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-honey-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optionnelle)
                </label>
                <textarea
                  value={serviceMarkPaidForm.note}
                  onChange={(e) => setServiceMarkPaidForm({ ...serviceMarkPaidForm, note: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-honey-500"
                  placeholder="Note sur le paiement..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document justificatif (optionnel)
                </label>
                <FileDropzone
                  onFileSelect={(file) => setServiceMarkPaidForm({ ...serviceMarkPaidForm, documentPaiement: file })}
                  uploadedFile={serviceMarkPaidForm.documentPaiement ? { name: serviceMarkPaidForm.documentPaiement.name } : null}
                  label="Ajouter un justificatif de paiement"
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ex: copie du chèque, reçu, etc. (PDF, JPG, PNG - max 10MB)
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowServiceMarkPaidModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={serviceMarkPaidSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {serviceMarkPaidSubmitting ? 'Enregistrement...' : 'Valider le paiement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal gestion caution */}
      {showCautionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {cautionForm.status === 'recu' ? 'Marquer la caution reçue' : 'Marquer la caution rendue'}
              </h3>
              <button
                type="button"
                onClick={() => setShowCautionModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateCaution} className="space-y-4">
              {cautionForm.status === 'recu' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de réception
                  </label>
                  <input
                    type="date"
                    required
                    value={cautionForm.dateReception}
                    onChange={(e) => setCautionForm({ ...cautionForm, dateReception: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optionnelle)
                </label>
                <textarea
                  value={cautionForm.note}
                  onChange={(e) => setCautionForm({ ...cautionForm, note: e.target.value })}
                  rows={3}
                  placeholder="Ex: Chèque n°123456..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {cautionForm.status === 'recu' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preuve de dépôt de caution (optionnel)
                  </label>
                  <FileDropzone
                    onFileSelect={(file) => setCautionForm({ ...cautionForm, documentCaution: file })}
                    uploading={uploadingCautionDocument}
                    uploadedFile={cautionForm.documentCaution ? { name: cautionForm.documentCaution.name } : null}
                    label="Ajouter une preuve de dépôt"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ex: photo du chèque, bordereau de dépôt... (PDF, JPG, PNG - max 10MB)
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCautionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={cautionSubmitting}
                  className={`px-4 py-2 text-white rounded-md text-sm font-medium disabled:opacity-50 ${
                    cautionForm.status === 'recu' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {cautionSubmitting ? 'Enregistrement...' : 'Valider'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale de sélection du type de document */}
      {showDocumentTypeSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Ajouter un nouveau document
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Sélectionnez le type de document à ajouter
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-3">
              <button
                onClick={() => {
                  setSelectedDocumentType('declaration_ruches');
                  setShowDocumentTypeSelectionModal(false);
                  setShowReuploadDocumentModal(true);
                }}
                className="w-full px-4 py-3 text-left border-2 border-gray-200 rounded-lg hover:border-honey-500 hover:bg-honey-50 transition-colors group"
              >
                <div className="flex items-center">
                  <DocumentIcon className="h-6 w-6 text-honey-600 group-hover:text-honey-700" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Déclaration de ruches</p>
                    <p className="text-xs text-gray-500">Ajouter une nouvelle déclaration de ruches</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setSelectedDocumentType('releve_exploitation_cgss');
                  setShowDocumentTypeSelectionModal(false);
                  setShowReuploadDocumentModal(true);
                }}
                className="w-full px-4 py-3 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors group"
              >
                <div className="flex items-center">
                  <BuildingLibraryIcon className="h-6 w-6 text-blue-600 group-hover:text-blue-700" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Attestation CGSS</p>
                    <p className="text-xs text-gray-500">Ajouter un relevé d'exploitation CGSS</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
              <button
                onClick={() => setShowDocumentTypeSelectionModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale d'upload de document */}
      {showReuploadDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedDocumentType === 'declaration_ruches' ? 'Ajouter une nouvelle déclaration de ruches' : 'Ajouter une attestation CGSS'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {selectedDocumentType === 'declaration_ruches' 
                  ? 'Téléchargez une nouvelle déclaration de ruches (l\'historique sera conservé)'
                  : 'Téléchargez un relevé d\'exploitation CGSS (l\'historique sera conservé)'}
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <FileDropzone
                onFileSelect={(file) => setNewDocumentFile(file)}
                accept="application/pdf,image/jpeg,image/png,image/*,.pdf,.jpg,.jpeg,.png"
                maxSize={5 * 1024 * 1024}
                label={selectedDocumentType === 'declaration_ruches' 
                  ? 'Cliquez ou déposez la nouvelle déclaration de ruches'
                  : 'Cliquez ou déposez l\'attestation CGSS'}
                uploading={reuploadingDocument}
                uploadedFile={newDocumentFile}
                required={true}
              />

              {newDocumentFile && !reuploadingDocument && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Info :</strong> Ce nouveau document sera ajouté à l'historique. Les anciens documents seront conservés.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReuploadDocumentModal(false);
                  setNewDocumentFile(null);
                  setSelectedDocumentType(null);
                }}
                disabled={reuploadingDocument}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                onClick={handleReuploadDocument}
                disabled={!newDocumentFile || reuploadingDocument}
                className="px-4 py-2 text-sm font-medium text-white bg-honey-600 rounded-md hover:bg-honey-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {reuploadingDocument ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Téléchargement...
                  </>
                ) : (
                  selectedDocumentType === 'declaration_ruches' ? 'Ajouter la déclaration' : 'Ajouter l\'attestation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal abandon de l'adhésion */}
      {showAbandonModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Abandon de l'adhésion
              </h3>
              <button
                type="button"
                onClick={() => setShowAbandonModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Fermer</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-2">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
                  <p className="text-sm text-yellow-800">
                    <strong>Attention :</strong> Cette action marquera l'adhésion comme abandonnée. 
                    L'adhérent ne pourra plus accéder aux services liés à cette adhésion.
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Êtes-vous sûr de vouloir marquer cette adhésion comme abandonnée ?
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowAbandonModal(false)}
                disabled={abandonSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAbandonAdhesion}
                disabled={abandonSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {abandonSubmitting ? 'Traitement...' : 'Confirmer l\'abandon'}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
  );
}
